/**
 * The decided sentences, in one table (rd r12 A5-4; rc-5 §1a, §3A).
 *
 * WHY THIS EXISTS. Every page used to invent its own answer to "what do we say
 * here": 92 decided sentences were re-typed across files, and page names were
 * typed by hand, so "Open Statements." drifted from the nav label "Statement
 * checks". A person then had to work out that the two names meant one page.
 *
 * WHAT LIVES HERE, AND WHAT DOES NOT. This table holds the sentences a
 * decision or a shared-sentence table already names, as a STATE the console
 * can show: no bank connected, a bank that needs reconnecting, the books not
 * built for a month, a statement that does not match, a card waiting on the
 * owner, an action nobody can do in the console. Every failure and outcome
 * sentence lives in `ui/failure.ts` (A5a's): 401, 403 (both roles), 404, 409,
 * 429, 5xx, network, "did not save", "Nothing changed.", "Nothing was saved",
 * "We could not confirm it saved". This module never repeats one, and the
 * duplicate-sentence guard fails a literal that appears in both files.
 *
 * NO NEW PRODUCT WORDING. Every string below is quoted from
 * docs/design/rd-z-fixes-background.md §3, docs/design/rd-r11-background.md §4
 * or docs/design/rd-r12-background.md §4, or is the page name the nav registry
 * publishes (`navLabel`). A row that needed words none of those name was not
 * added; it is flagged in the lane's PR instead.
 *
 * ROLE RULE (rd-polish-rules.md §1): the owner is the person reading, so the
 * owner is never told to ask, tell or escalate to the owner. The account
 * manager keeps "Ask the owner". A row that differs by role says so here, once.
 */

import { destinationsForRole, type ShellRoute } from "../nav-registry.js";
import type { CardRuleNotAddedReason, StaffRole } from "../types.js";

/** The values a decided sentence interpolates. */
export type SentenceVars = Readonly<Record<string, string>>;

/**
 * Every decided sentence, by key. The type is derived from this list, so a key
 * cannot exist in the type and not in the table (the table is typed as a full
 * `Record<SentenceKey, …>`).
 */
export const SENTENCE_KEYS = [
  /** decision 3 (with resolution E5): the last booking run failed. */
  "booking_failed",
  /** rd-z §3: the books could not be built for a month. */
  "books_not_built",
  /** rd-z §3: a status read failed and the last reading is still shown. */
  "status_read_failed",
  /** rd-z §3: the bank needs a sign-in. */
  "bank_needs_reconnect",
  /** rd-z §3 / rd-r11 §4: no bank is connected yet. */
  "no_bank_connected",
  /** rd-z §3: a statement does not match the books. */
  "statement_mismatch",
  /** rd-z §3: the Months one-liner for the same state. */
  "statement_mismatch_one_liner",
  /**
   * rd13 L4: how far apart a statement and the bank are, and on what date —
   * the statement is the double check, so the gap is said, not left to be
   * subtracted (owner 2026-10-31: "it is just a double check basically").
   */
  "statement_mismatch_difference",
  /** rd13 L4: a mismatch the page cannot put a number on (no bank figure). */
  "statement_mismatch_no_bank",
  /** rd13 L4: a mismatch the page cannot put a number on (no statement figure). */
  "statement_mismatch_no_statement",
  /** rd13 L4: both figures are there and equal, so there is no gap to name. */
  "statement_mismatch_same",
  /** rd-z §3: a preview could not load. */
  "preview_unavailable",
  /** rd-r11 §4: a bank link works for 30 minutes. */
  "bank_link_works_for",
  /** rd-r11 §4: mapping a new company's first bank account. */
  "first_bank_mapping",
  /** decision 2: an action nobody can do in the console. */
  "ledger_by_hand",
  /** decision 8 (resolution E4): an unsigned rule matches both directions. */
  "rule_matches_both_directions",
  /** decision 8: a money-in rule matches deposits only. */
  "rule_matches_deposits",
  /** decision 8: a money-out rule matches payments only. */
  "rule_matches_payments",
  /** decision 11: a card only the owner can answer, on the AM's Months page. */
  "waiting_for_owner",
  /** decision 9: cards outside the 24-month window. */
  "outside_months_shown",
  /** decision 12: send a card to the hub. */
  "send_to_hub",
  /** decision 14: a company whose name is unknown, in a sentence. */
  "company_unknown",
  /** decision 14: a company whose name is unknown, as a page heading. */
  "company_heading",
  /**
   * Home/Months page words that were typed in two files (B0 left the pair to
   * this lane). Moved here by B2a so the duplicate-sentence guard has one home;
   * the words are the pages' existing words, not new product wording.
   */
  "booking_failed_short",
  "booking_running",
  "books_view_off",
  "statements_not_match_tail",
  /**
   * decision 2's neighbour (round 2 review, M1): a switch turned on outside the
   * console. It is not ledger content, so it must not read "added in the ledger
   * by hand" — the Books view is GitHub variables and the hub intake is D1
   * settings loaded by a script.
   */
  "switched_outside_console",
  /**
   * rd13 L6 review 1: what the Rules page says about a card answer whose
   * standing rule is not in rules.yaml. The reason codes are the pipeline's
   * closed set (review_queue.make_rule_reason, migration 0082); a person reads
   * the sentence, never the code. Moved here from pages/rules.ts, where the
   * nine were page-local and two of them were typed twice in one file.
   */
  "card_rule_waiting",
  "card_rule_not_added_conflict",
  "card_rule_not_added_place",
  "card_rule_not_added_direction",
  "card_rule_not_added_name",
  "card_rule_not_added_text",
  "card_rule_not_added_vendor",
  "card_rule_not_added_not_booked",
  "card_rule_not_added_replaced",
  "card_rule_not_added_other",
] as const;

export type SentenceKey = (typeof SENTENCE_KEYS)[number];

/** One row: the words for a role, with the vars the row names. */
type Row = (role: StaffRole, vars: SentenceVars) => string;

/**
 * A var a row cannot say its sentence without. A missing var throws rather than
 * printing `undefined` (or a placeholder) at a person; every caller passes the
 * vars its row names, and the lane's test renders every row for both roles.
 */
function need(vars: SentenceVars, name: string): string {
  const value = vars[name];
  if (value === undefined || value.trim() === "") {
    throw new Error(`sentence: this row needs the "${name}" value`);
  }
  return value;
}

/** The same words for both roles. */
function both(words: string): Row {
  return () => words;
}

/** A row whose next step differs by role. */
function byRole(owner: string, accountManager: string): Row {
  return (role) => (role === "owner" ? owner : accountManager);
}

/**
 * The page name for a route, from `nav-registry.ts` (read-only import). The
 * registry is the one place a page is named, so a sentence cannot drift from
 * the nav: "Open Statements." became "Open Statement checks." by using this.
 *
 * A route the registry does not publish throws. Printing the route key would
 * put an internal name (`receipt-shelf`) on a face, which is the defect this
 * function exists to prevent.
 */
export function navLabel(route: ShellRoute): string {
  const label = NAV_LABELS.get(route);
  if (label === undefined) {
    throw new Error(`navLabel: no nav label for route "${route}"`);
  }
  return label;
}

/**
 * Every route's label, built from the registry's own destinations for both
 * roles. The owner and the account manager see the same labels, so the first
 * label published for a route wins (the two agree today; the guard's
 * registry-drift test asserts it).
 */
const NAV_LABELS: ReadonlyMap<ShellRoute, string> = (() => {
  const labels = new Map<ShellRoute, string>();
  for (const role of ["owner", "account_manager"] as const) {
    for (const destination of destinationsForRole(role)) {
      if (!labels.has(destination.id)) labels.set(destination.id, destination.label);
    }
  }
  return labels;
})();

export const SENTENCES: Readonly<Record<SentenceKey, Row>> = {
  // DECIDED (owner, decision 3; resolution E5: keep the cause, then the action).
  // The AM can press Check now, so the AM is not told to ask the owner. The
  // owner's sentence is unchanged from bookingFailedCopy("owner").
  booking_failed: byRole(
    `The last booking run failed. Check ${navLabel("health")}, then press Check now.`,
    "The last booking run failed. Press Check now to try again.",
  ),
  books_not_built: byRole(
    `The books could not be built for {month}. Try again. If it keeps happening, check ${navLabel("health")}.`,
    "The books could not be built for {month}. Try again, or ask the owner.",
  ),
  status_read_failed: both(
    "We could not check just now. Last known: {state}, checked {when}.",
  ),
  bank_needs_reconnect: byRole(
    "Needs reconnecting.",
    "Ask the owner to reconnect the bank.",
  ),
  no_bank_connected: byRole(
    "No bank connected yet.",
    "No bank connected yet. Ask the owner to connect one.",
  ),
  statement_mismatch: both(
    "The books show {books}. The bank shows {bank}. The statement shows {statement}.",
  ),
  statement_mismatch_one_liner: both(
    `The statement for {account} does not match the books. Open ${navLabel("statements")}.`,
  ),
  // rd13 L4. The comparator is the BANK's own figure: that is the pair the
  // check compares (pipeline/pdf_reconcile.py — the statement's ending balance
  // against the Plaid balance snapshot for the closing date). The books figure
  // is a third number, dated at month end and possibly behind by bank lines not
  // booked yet, so a "higher than the books" difference would subtract two
  // different dates and make the gap say something the check never measured.
  statement_mismatch_difference: both(
    "The statement is {difference} {side} than the bank on {date}.",
  ),
  statement_mismatch_no_bank: both(
    "The statement shows {statement}. No bank balance was read for {date}, so the two could not be compared.",
  ),
  statement_mismatch_no_statement: both(
    "No statement balance was read for {date}, so the bank and the statement could not be compared.",
  ),
  statement_mismatch_same: both(
    "The bank and the statement both show {amount} on {date}.",
  ),
  preview_unavailable: both("Preview unavailable."),
  bank_link_works_for: both("works for 30 minutes"),
  first_bank_mapping: both(
    "This bank account isn't connected to the books yet, so its lines are on hold. It can't be connected from the console yet. Once it's connected, the held lines book themselves.",
  ),
  // DECIDED (owner, decision 2).
  ledger_by_hand: both("This has to be added in the ledger by hand for now."),
  rule_matches_both_directions: both("every payment or deposit"),
  rule_matches_deposits: both("every deposit"),
  rule_matches_payments: both("every payment"),
  waiting_for_owner: both("Waiting for the owner"),
  outside_months_shown: both("Outside the months shown"),
  send_to_hub: both("Send to the hub"),
  company_unknown: both("this company"),
  company_heading: both("Company"),
  booking_failed_short: both("The last booking run failed."),
  booking_running: both("Booking is running now."),
  books_view_off: both("The books view is not switched on for this company."),
  statements_not_match_tail: both(" not match the books."),
  // Round 2 review, M1 (orchestrator wording): neither switch is ledger
  // content. The Books view is GitHub variables; the hub intake is D1
  // settings. Both are turned on outside the console for now.
  switched_outside_console: both("This is switched on outside the console for now."),
  // rd13 L6 review 1: the card-rule states, one row each. The words are the
  // lane's own (the brief asked for them); the home is here so no page retypes
  // them and a reason code can never reach a face.
  card_rule_waiting: both("Waiting to be applied. It starts on the next update."),
  card_rule_not_added_conflict: both(
    "Not added. It clashes with a rule that is already in use.",
  ),
  card_rule_not_added_place: both("Not added. That place cannot be used for a rule."),
  card_rule_not_added_direction: both(
    "Not added. This payee is used for both payments and deposits, so one rule would book the wrong one.",
  ),
  card_rule_not_added_name: both(
    "Not added. There is no vendor name or description to match.",
  ),
  card_rule_not_added_text: both(
    "Not added. The words chosen for it are not in the description.",
  ),
  card_rule_not_added_vendor: both(
    "Not added as a rule. This card saved a vendor name instead.",
  ),
  card_rule_not_added_not_booked: both(
    "Not added. The transaction did not book, so no rule was made.",
  ),
  card_rule_not_added_replaced: both("Not added. A later answer replaced this one."),
  card_rule_not_added_other: both(
    "Not added. Books could not make a rule from this card.",
  ),
};

/**
 * The sentence key for each reason a card's standing rule was not added.
 * Total over the pipeline's closed set: a reason the page does not know (a
 * newer pipeline value) reads as the general sentence, never as a code.
 */
const CARD_RULE_NOT_ADDED_KEYS: Readonly<
  Record<CardRuleNotAddedReason, SentenceKey>
> = {
  conflict: "card_rule_not_added_conflict",
  place: "card_rule_not_added_place",
  direction: "card_rule_not_added_direction",
  name: "card_rule_not_added_name",
  text: "card_rule_not_added_text",
  vendor: "card_rule_not_added_vendor",
  not_booked: "card_rule_not_added_not_booked",
  replaced: "card_rule_not_added_replaced",
  other: "card_rule_not_added_other",
};

/** Why a card answer's standing rule was not added, in plain words. */
export function cardRuleNotAdded(
  reason: CardRuleNotAddedReason | null,
  role: StaffRole,
): string {
  const key =
    reason !== null && Object.hasOwn(CARD_RULE_NOT_ADDED_KEYS, reason)
      ? CARD_RULE_NOT_ADDED_KEYS[reason]
      : CARD_RULE_NOT_ADDED_KEYS.other;
  return sentence(key, role);
}

/**
 * The decided sentence for `key`, read as `role` reads it. `vars` fills the
 * values the row interpolates (`{month}`, `{books}` …); a row that names one
 * and is not given it throws rather than printing a placeholder.
 */
export function sentence(
  key: SentenceKey,
  role: StaffRole,
  vars: SentenceVars = {},
): string {
  const row = SENTENCES[key];
  // Every template's `{name}` is replaced here, so no row can print one.
  return row(role, vars).replace(/\{(\w+)\}/g, (_match, name: string) => need(vars, name));
}
